// The Curve tab's editor (spec clip-speed-curves-tab, layout A): the clip's curve over its file span,
// speed up on a log scale from 0.25× to 4×, its points dots to drag, the timeline's playhead across
// it, and Add point, Delete point and Reset under it. A drag is one edit, made when let go.
import { Button, Group } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { SPEED_MARKS, addPoint, dragPoint, nearestGap, removePoint, speedOfY, speedY } from "./curve-edit";
import { AtPlayhead, usePlayheadWatch, type Playhead } from "./playhead";
import { curveOver, fileMsAt, type CurvePoint } from "./speed-curve";
import { spanOf, type AudioClip, type VideoClip } from "./timeline";
import classes from "./curves.module.css";

/** The editor's height, and the room its labels take on the left. */
const HEIGHT = 150;
const PAD = { left: 30, right: 10, top: 10, bottom: 10 } as const;
/** An arrow key moves a point an eighth of a halving up or down, or a hundredth of the span along. */
const KEY_SPEED = 2 ** (1 / 8);
const KEY_PLACE = 0.01;

const speedText = (speed: number) => `${Number(speed.toFixed(2))}×`;

export function CurveEditor(props: {
  clip: VideoClip | AudioClip;
  playhead: Playhead;
  disabled: boolean;
  /** A changed curve, as places of the span: one edit. */
  onCurve: (points: readonly CurvePoint[]) => void;
  onReset: () => void;
}) {
  const { clip, playhead, disabled, onCurve } = props;
  const spanMs = spanOf(clip);
  const gap = nearestGap(spanMs);
  const shown = useMemo(() => curveOver(clip.speedPoints, clip.inMs, spanMs), [clip.speedPoints, clip.inMs, spanMs]);
  // A drag's curve, with the curve it began from: shown only while that is still the clip's, so a
  // drag that never ended hides no edit made since (a preset, Reset, Undo).
  const [draft, setDraft] = useState<{ from: readonly CurvePoint[]; curve: readonly CurvePoint[] } | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  // The point being dragged, by which pointer, the curve it began from, whether it moved, and the
  // curve it makes, read when it is let go.
  const drag = useRef<{
    index: number;
    pointer: number;
    from: readonly CurvePoint[];
    moved: boolean;
    curve: readonly CurvePoint[];
  } | null>(null);
  const { ref, width } = useElementSize();
  const svg = useRef<SVGSVGElement>(null);
  const points = draft !== null && draft.from === shown ? draft.curve : shown;
  const room = { width: Math.max(1, width - PAD.left - PAD.right), height: HEIGHT - PAD.top - PAD.bottom };
  const xOf = (at: number) => PAD.left + at * room.width;
  const yOf = (speed: number) => PAD.top + speedY(speed, room.height);
  const picked = chosen !== null && chosen < shown.length ? chosen : null;

  /** The place of the span the clip reads at a time of the timeline, or null off the clip. */
  const placeAt = (atMs: number) => (atMs < clip.startMs || atMs >= clip.endMs ? null : (fileMsAt(clip, atMs) - clip.inMs) / spanMs);
  const addable = usePlayheadWatch(playhead, (atMs) => {
    const at = placeAt(atMs);
    return at !== null && addPoint(shown, at, gap) !== null;
  });

  const fromPointer = (event: PointerEvent<HTMLButtonElement>) => {
    const box = svg.current?.getBoundingClientRect();
    if (box === undefined) return null;
    return { at: (event.clientX - box.left - PAD.left) / room.width, speed: speedOfY(event.clientY - box.top - PAD.top, room.height) };
  };
  const onDown = (index: number) => (event: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { index, pointer: event.pointerId, from: shown, moved: false, curve: shown };
  };
  /**
   * The drag of `index` by this event's pointer, if one is on. One whose curve changed since it
   * began (a preset, Reset, Undo, a key, another touch) is over, with no edit: its moves and its
   * release do nothing, so it never writes its old curve over the newer one.
   */
  const heldBy = (index: number, event: PointerEvent<HTMLButtonElement>) => {
    const held = drag.current;
    if (held?.index !== index || held.pointer !== event.pointerId) return null;
    if (held.from === shown) return held;
    drag.current = null;
    setDraft(null);
    return null;
  };
  const onMove = (index: number) => (event: PointerEvent<HTMLButtonElement>) => {
    const held = heldBy(index, event);
    const to = fromPointer(event);
    if (held === null || to === null) return;
    held.moved = true;
    held.curve = dragPoint(shown, index, to.at, to.speed, gap);
    setDraft({ from: shown, curve: held.curve });
  };
  const onUp = (index: number) => (event: PointerEvent<HTMLButtonElement>) => {
    const held = heldBy(index, event);
    if (held === null) return;
    drag.current = null;
    setDraft(null);
    if (held.moved) onCurve(held.curve);
  };
  // A drag the browser cancels, or whose pointer capture is lost, ends with no edit.
  const onCancel = (index: number) => (event: PointerEvent<HTMLButtonElement>) => {
    if (heldBy(index, event) === null) return;
    drag.current = null;
    setDraft(null);
  };
  const remove = (index: number | null) => {
    const left = index === null ? null : removePoint(shown, index);
    if (left === null) return;
    setChosen(null);
    onCurve(left);
  };
  // Arrows move the focused point, as a drag does, an edit each; Delete takes it away.
  const onKey = (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
    const point = shown[index];
    if (disabled || point === undefined) return;
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      remove(index);
      return;
    }
    const step = { ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowRight: [1, 0], ArrowLeft: [-1, 0] }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const [along = 0, up = 0] = step;
    setChosen(index);
    onCurve(dragPoint(shown, index, point.at + along * KEY_PLACE, point.speed * KEY_SPEED ** up, gap));
  };

  const path = points.map((point, at) => `${at === 0 ? "M" : "L"}${xOf(point.at).toFixed(1)} ${yOf(point.speed).toFixed(1)}`).join(" ");
  return (
    <>
      <div ref={ref} className={classes.editor} data-curve-editor>
        <svg ref={svg} aria-hidden="true">
          {SPEED_MARKS.map((speed) => (
            <g key={speed}>
              <line className={classes.mark} data-one={speed === 1 || undefined} x1={PAD.left} x2={PAD.left + room.width} y1={yOf(speed)} y2={yOf(speed)} />
              <text className={classes.label} x={2} y={yOf(speed) + 3}>
                {speedText(speed)}
              </text>
            </g>
          ))}
          <path className={classes.curve} d={path} />
          <AtPlayhead store={playhead}>
            {(atMs) => {
              const at = placeAt(atMs);
              return at === null ? null : (
                <line className={classes.playhead} x1={xOf(at)} x2={xOf(at)} y1={PAD.top} y2={PAD.top + room.height} data-playhead />
              );
            }}
          </AtPlayhead>
        </svg>
        {points.map((point, index) => (
          <button
            // Its index is its place in the curve: a drag holds each point between its neighbours.
            key={index}
            type="button"
            className={classes.point}
            style={{ left: xOf(point.at), top: yOf(point.speed) }}
            aria-label={`Curve point ${index + 1} of ${points.length}: ${speedText(point.speed)}`}
            aria-pressed={picked === index}
            disabled={disabled}
            onClick={() => setChosen(index)}
            onPointerDown={onDown(index)}
            onPointerMove={onMove(index)}
            onPointerUp={onUp(index)}
            onPointerCancel={onCancel(index)}
            onLostPointerCapture={onCancel(index)}
            onKeyDown={onKey(index)}
          />
        ))}
      </div>
      <Group gap={6}>
        <Button
          size="compact-xs"
          variant="default"
          disabled={disabled || !addable}
          onClick={() => {
            const at = placeAt(playhead.get());
            const added = at === null ? null : addPoint(shown, at, gap);
            if (added !== null) onCurve(added);
          }}
        >
          Add point
        </Button>
        <Button
          size="compact-xs"
          variant="default"
          disabled={disabled || picked === null || removePoint(shown, picked) === null}
          onClick={() => remove(picked)}
        >
          Delete point
        </Button>
        <Button size="compact-xs" variant="default" disabled={disabled} onClick={props.onReset}>
          Reset
        </Button>
      </Group>
    </>
  );
}
